import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useStytch, useStytchSession } from "@stytch/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AUTHENTICATE__DYNAMIC,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
  ROOT,
} from "@/lib/links";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";
import { useInvalidateUserStats } from "@/lib/queries/user/useUserStats";
import { useTranslation } from "react-i18next";
import OAuth from "@/components/auth/OAuth";

const createSignupSchema = (
  useMagicLink: boolean,
  t: (key: string) => string
) => {
  const baseSchema = z.object({
    email: z.string().email({ message: t("signup.validation.emailRequired") }),
    agreeTerms: z.boolean().refine((val) => val === true, {
      message: t("signup.validation.termsRequired"),
    }),
  });

  if (useMagicLink) {
    return baseSchema.extend({
      password: z.string().optional(),
      confirmPassword: z.string().optional(),
    });
  }

  return baseSchema
    .extend({
      password: z
        .string()
        .min(8, { message: t("signup.validation.passwordMinLength") }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("signup.validation.passwordsMatch"),
      path: ["confirmPassword"],
    });
};

export default function Signup() {
  const { t } = useTranslation("auth");
  const stytchClient = useStytch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const invalidateUserStats = useInvalidateUserStats();
  const [useMagicLink, setUseMagicLink] = useState(false);

  const signupSchema = createSignupSchema(useMagicLink, t);
  type SignupFormValues = z.infer<typeof signupSchema>;

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      agreeTerms: false,
    },
  });

  // Magic link signup mutation
  const sendMagicLinkMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await stytchClient.magicLinks.email.loginOrCreate(
        email,
        {
          login_magic_link_url: `${import.meta.env.VITE_DOMAIN}/authenticate${returnTo !== "" && returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`,
          login_expiration_minutes: 60 * 24 * 7,
          signup_magic_link_url: `${import.meta.env.VITE_DOMAIN}/authenticate${returnTo !== "" && returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`,
          signup_expiration_minutes: 60 * 24 * 7,
        }
      );

      if (response.status_code !== 200) {
        throw new Error("Failed to send magic link");
      }

      return response;
    },
    onSuccess: () => {
      toast({
        title: t("signup.toast.magicLinkSent.title"),
        description: t("signup.toast.magicLinkSent.description"),
      });
    },
    onError: (error: any) => {
      toast({
        title: t("signup.toast.magicLinkFailed.title"),
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  // Create account mutation
  const createAccountMutation = useMutation({
    mutationFn: async (data: SignupFormValues) => {
      if (useMagicLink) {
        // Send magic link for signup
        return sendMagicLinkMutation.mutateAsync(data.email);
      }

      // Create user with password
      const response = await stytchClient.passwords.create({
        email: data.email,
        password: data.password!,
        session_duration_minutes: 60 * 24 * 7,
      });

      return response;
    },
    onSuccess: async (_, variables) => {
      if (useMagicLink) {
        // Magic link flow - don't navigate, user needs to check email
        return;
      }

      toast({
        title: t("signup.toast.accountCreated.title"),
        description: t("signup.toast.accountCreated.description"),
      });
      queryClient.invalidateQueries({ queryKey: "init" });
      queryClient.refetchQueries({ queryKey: "init" });
      queryClient.invalidateQueries({ queryKey: "user" });

      queryClient.refetchQueries({ queryKey: "user" });
      // Invalidate user stats when new account is created
      invalidateUserStats();
      // Authenticate the user after signup
      try {
        await stytchClient.passwords.authenticate({
          email: variables.email,
          password: variables.password!,
          session_duration_minutes: 60 * 24 * 7, // 7 days
        });

        navigate(decodeURIComponent(returnTo));
      } catch (error) {
        // Handle authentication error
        toast({
          title: t("signup.toast.accountCreatedLoginFailed.title"),
          description: t("signup.toast.accountCreatedLoginFailed.description"),
          variant: "destructive",
        });
        navigate(
          `/authenticate${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`
        );
      }
    },
    onError: (error: any) => {
      // Display error message
      toast({
        title: useMagicLink
          ? t("signup.toast.magicLinkFailed.title")
          : t("signup.toast.accountCreationFailed.title"),
        description: error.message || "An error occurred during signup",
        variant: "destructive",
      });
    },
  });
  const [searchParams, _] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/";

  const stytch = useStytchSession();

  useEffect(() => {
    if (stytch.session !== null) {
      invalidateUserStats();
      window.location.href = decodeURIComponent(returnTo);
    }
  }, [stytch.session, returnTo, invalidateUserStats]);
  useEffect(() => {
    if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "magic_links"
    ) {
      stytchClient.magicLinks.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    } else if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "login"
    ) {
      stytchClient.magicLinks.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    } else if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "oauth"
    ) {
      stytchClient.oauth.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    }
  }, [searchParams, stytchClient]);

  // Form submission handler
  const onSubmit = form.handleSubmit(async (data) => {
    // Prevent default form submission
    if (createAccountMutation.isPending) return;

    // Additional validation for magic link mode
    if (useMagicLink) {
      // For magic link, we only need email and terms agreement
      const emailSchema = z.object({
        email: z
          .string()
          .email({ message: t("signup.validation.emailRequired") }),
        agreeTerms: z.boolean().refine((val) => val === true, {
          message: t("signup.validation.termsRequired"),
        }),
      });

      const validation = emailSchema.safeParse({
        email: data.email,
        agreeTerms: data.agreeTerms,
      });

      if (!validation.success) {
        // Set form errors
        validation.error.errors.forEach((error) => {
          form.setError(error.path[0] as keyof SignupFormValues, {
            message: error.message,
          });
        });
        return;
      }
    }

    // Trigger the create account mutation
    createAccountMutation.mutate(data);
  });

  return (
    <div className="flex flex-row w-full h-svh">
      <div className="relative hidden w-full h-full px-10 sm:block py-9">
        <img
          src={"/assets/login-c.png"}
          className="object-cover w-full h-full rounded-[36px]"
        />
        <h1 className="absolute flex flex-row whitespace-nowrap gap-2 text-4xl font-semibold text-white bottom-24 right-12 text-[40px] xl:bottom-52 xl:right-44 xl:text-[79px] items-center">
          <GraduationCap size={60} />
          Teacher Tools
        </h1>
      </div>
      <main className="flex flex-col items-center justify-center w-full h-full p-6">
        <Button variant="secondary" className="self-start mb-4 ml-4" asChild>
          <Link to={ROOT}>
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
        </Button>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-primary">
              {t("signup.title")}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <FormProvider {...form}>
              <form onSubmit={onSubmit} className="space-y-4">
                {createAccountMutation.error && (
                  <div className="p-3 mb-4 text-sm rounded-md bg-destructive text-destructive-foreground">
                    {(createAccountMutation.error as Error).message ||
                      "An error occurred"}
                  </div>
                )}

                {/* Magic Link Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="space-y-0.5">
                    <div className="text-base font-medium">
                      {t("signup.magicLink.title")}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t("signup.magicLink.description")}
                    </div>
                  </div>
                  <Switch
                    checked={useMagicLink}
                    onCheckedChange={(checked) => {
                      setUseMagicLink(checked);
                      // Reset password fields when toggling
                      if (checked) {
                        form.setValue("password", "");
                        form.setValue("confirmPassword", "");
                        form.clearErrors("password");
                        form.clearErrors("confirmPassword");
                      }
                    }}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="email">
                        {t("signup.email.label")}
                      </FormLabel>
                      <Input
                        type="email"
                        placeholder={t("signup.email.placeholder")}
                        {...field}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Conditionally render password fields */}
                {!useMagicLink && (
                  <>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="password">
                            {t("signup.password.label")}
                          </FormLabel>
                          <Input type="password" {...field} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="confirmPassword">
                            {t("signup.confirmPassword.label")}
                          </FormLabel>
                          <Input type="password" {...field} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <FormField
                  control={form.control}
                  name="agreeTerms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="w-4 h-4"
                      />
                      <div className="space-y-1 leading-none">
                        <FormLabel htmlFor="agreeTerms" className="text-sm">
                          {t("signup.agreeTerms.text")}{" "}
                          <Link
                            to={TERMS_OF_SERVICE}
                            className="underline decoration-dashed text-primary"
                          >
                            {t("signup.agreeTerms.termsLink")}
                          </Link>{" "}
                          {t("signup.agreeTerms.and")}{" "}
                          <Link
                            to={PRIVACY_POLICY}
                            className="underline decoration-dashed text-primary"
                          >
                            {t("signup.agreeTerms.privacyLink")}
                          </Link>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={createAccountMutation.isPending}
                >
                  {createAccountMutation.isPending
                    ? useMagicLink
                      ? t("signup.button.sendingMagicLink")
                      : t("signup.button.creating")
                    : useMagicLink
                      ? t("signup.button.sendMagicLink")
                      : t("signup.button.signUp")}
                </Button>
              </form>
            </FormProvider>
            <div className="mt-4">
              <p className="font-semibold text-center text-light-grey">
                {t("signup.oauth")}
              </p>
              <OAuth returnTo={returnTo} />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <div className="text-sm text-center text-gray-500">
              {t("signup.alreadyHaveAccount")}{" "}
              <Link
                to={AUTHENTICATE__DYNAMIC(returnTo)}
                className="underline text-primary"
              >
                {t("signup.signInLink")}
              </Link>
            </div>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}
